import type { components } from '@/lib/api/schema'

/** Membership roles, lowest to highest privilege, straight from the API contract. */
export type Role = components['schemas']['Role']

const LABELS: Record<Role, string> = {
  viewer: 'Read-only',
  sales: 'Salesperson',
  service: 'Service',
  manager: 'Manager',
  admin: 'Administrator',
  owner: 'Owner',
}

/** Membership role -> the label the brief uses for it. Unknown roles show as-is. */
export function roleLabel(role: string): string {
  return LABELS[role as Role] ?? role
}

/**
 * Privilege order. Position in this list is the whole rule.
 *
 * A literal tuple rather than `readonly Role[]`, so the rungs survive into the types that read
 * it: the staff screen builds a zod enum and a menu straight off this, and neither should have
 * to restate the six names to get them back.
 */
export const ROLE_ORDER = [
  'viewer',
  'sales',
  'service',
  'manager',
  'admin',
  'owner',
] as const satisfies readonly Role[]

/**
 * What a screen needs, not what a role is: `read` any member, `sell` create/edit/photos,
 * `manage` disable/enable/re-sync/import, `administer` dealership settings. Mirrors the role
 * rules the API enforces, so a hidden action is also a rejected request.
 */
export type Capability = 'read' | 'sell' | 'manage' | 'administer'

const MINIMUM_RANK: Record<Capability, number> = {
  read: ROLE_ORDER.indexOf('viewer'),
  sell: ROLE_ORDER.indexOf('sales'),
  manage: ROLE_ORDER.indexOf('manager'),
  administer: ROLE_ORDER.indexOf('admin'),
}

/**
 * `MembershipSummary.role` is a plain string on the wire, so anything can arrive here. A role
 * outside `ROLE_ORDER` ranks -1, below every real one: unrecognised, empty and absent roles are
 * never permitted.
 */
export function can(role: string | null | undefined, capability: Capability): boolean {
  const held = ROLE_ORDER.findIndex((known) => known === role)
  return held >= 0 && held >= MINIMUM_RANK[capability]
}
